import { NavigationTabs } from './NavigationTabs.js';

export class LiveChatPanel {
	constructor(state, options = {}) {
		this.state = state;
		this.options = {
			position: options.position || 'right',
			...options,
		};
		this.element = null;
		this.navigationTabs = null;
		this.currentViewComponent = null;
		this.viewRegistry = {};
		this._unsubscribe = null;
		this._expanded = false;
	}

	registerView(name, ViewClass) {
		this.viewRegistry[name] = ViewClass;
	}

	render() {
		this.element = document.createElement('div');
		this.element.className = `liveChat-panel liveChat-panel-${this.options.position}`;

		this.element.innerHTML = `
			<div class="liveChat-panel-content">
				<div class="liveChat-connection-banner" role="status" hidden></div>
				<div class="liveChat-panel-views"></div>
				<div class="liveChat-panel-nav"></div>
			</div>
		`;

		this.options.onToggleExpand = () => this._toggleExpand();
		this.options.isExpanded = () => this._expanded;

		this.navigationTabs = new NavigationTabs(this.state, this.options);
		const navContainer = this.element.querySelector('.liveChat-panel-nav');
		navContainer.appendChild(this.navigationTabs.render());

		this._renderCurrentView();
		this._updateConnectionBanner();

		this.element.addEventListener('keydown', (e) => this._handleKeydown(e));

		this._unsubscribe = this.state.subscribe((type) => {
			if (type === 'viewChange') {
				this._renderCurrentView();
			} else if (type === 'connectionStateChange') {
				this._updateConnectionBanner();
			}
		});

		return this.element;
	}

	_handleKeydown(e) {
		if (e.defaultPrevented) return;

		if (e.key === 'Escape') {
			if (e.target.closest?.('emoji-picker')) return;
			const openMenu = this.element.querySelector(
				'.liveChat-chat-menu-dropdown.open'
			);
			if (openMenu) {
				openMenu.classList.remove('open');
				return;
			}
			this.state.setOpen(false);
			return;
		}

		// Clickable rows and cards are divs; let Enter/Space activate them
		if (
			(e.key === 'Enter' || e.key === ' ') &&
			e.target.matches?.('div[role="button"]')
		) {
			e.preventDefault();
			e.target.click();
		}
	}

	_updateConnectionBanner() {
		const banner = this.element.querySelector('.liveChat-connection-banner');
		if (!banner) return;
		const state = this.state.connectionState;
		let text = '';
		let variant = '';
		if (state === 'reconnecting') {
			text = 'Reconnecting…';
			variant = 'reconnecting';
		} else if (state === 'offline') {
			text = "You're offline";
			variant = 'offline';
		}
		if (!text) {
			banner.hidden = true;
			banner.textContent = '';
			banner.className = 'liveChat-connection-banner';
			return;
		}
		banner.hidden = false;
		banner.textContent = text;
		banner.className = `liveChat-connection-banner liveChat-connection-banner-${variant}`;
	}

	_toggleExpand() {
		this._expanded = !this._expanded;
		this.element.classList.toggle('liveChat-panel-expanded', this._expanded);

		const menuIcon = this.element.querySelector(
			'.liveChat-expand-window-btn iconify-icon'
		);
		const menuLabel = this.element.querySelector(
			'.liveChat-expand-window-btn span'
		);
		if (menuIcon)
			menuIcon.setAttribute(
				'icon',
				this._expanded ? 'ph:arrows-in' : 'ph:arrows-out'
			);
		if (menuLabel)
			menuLabel.textContent = this._expanded
				? 'Collapse window'
				: 'Expand window';
	}

	_renderCurrentView() {
		const viewsContainer = this.element.querySelector('.liveChat-panel-views');
		const navContainer = this.element.querySelector('.liveChat-panel-nav');

		if (this.currentViewComponent && this.currentViewComponent.destroy) {
			this.currentViewComponent.destroy();
		}

		viewsContainer.innerHTML = '';

		// Collapse expanded state when leaving chat view
		if (this.state.currentView !== 'chat' && this._expanded) {
			this._expanded = false;
			this.element.classList.remove('liveChat-panel-expanded');
		}

		const ViewClass = this.viewRegistry[this.state.currentView];
		if (ViewClass) {
			this.currentViewComponent = new ViewClass(this.state, this.options);
			viewsContainer.appendChild(this.currentViewComponent.render());
		} else {
			viewsContainer.innerHTML = `<div class="liveChat-empty-state">
				<p>View not found: ${this.state.currentView}</p>
			</div>`;
		}

		// Hide nav in chat, prechat and feedback views
		if (navContainer) {
			const hideNav =
				this.state.currentView === 'chat' ||
				this.state.currentView === 'prechat' ||
				this.state.currentView === 'feedback';
			navContainer.style.display = hideNav ? 'none' : '';
		}
	}

	show() {
		if (this.element) {
			requestAnimationFrame(() => {
				this.element.classList.add('open');
			});
		}
	}

	hide() {
		if (this.element) {
			this.element.classList.remove('open');
		}
	}

	setHeader(headerContent) {
		const header = this.element.querySelector('.liveChat-panel-header');
		if (header) {
			header.innerHTML = headerContent;
		}
	}

	destroy() {
		if (this._unsubscribe) {
			this._unsubscribe();
		}
		if (this.navigationTabs) {
			this.navigationTabs.destroy();
		}
		if (this.currentViewComponent && this.currentViewComponent.destroy) {
			this.currentViewComponent.destroy();
		}
		if (this.element && this.element.parentNode) {
			this.element.parentNode.removeChild(this.element);
		}
	}
}
